import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Inbox, Target, ShieldAlert, Check, ChevronDown, ChevronUp, Sparkles, Users, Star } from 'lucide-react';
import { NewsletterForm } from '../components/NewsletterForm';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';

/**
 * Hook utilitaire : injecte title + meta description + canonical + JSON-LD
 * dans le <head> du document. Nettoie au démontage.
 */
function usePageMeta({ title, description, canonical, jsonLd }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const upsert = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement(selector.split('[')[0]);
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return { el, created };
    };

    const refs = [];
    if (description) {
      refs.push(upsert('meta[name="description"]', { name: 'description', content: description }));
      refs.push(upsert('meta[property="og:description"]', { property: 'og:description', content: description }));
    }
    if (title) {
      refs.push(upsert('meta[property="og:title"]', { property: 'og:title', content: title }));
    }
    if (canonical) {
      refs.push(upsert('link[rel="canonical"]', { rel: 'canonical', href: canonical }));
      refs.push(upsert('meta[property="og:url"]', { property: 'og:url', content: canonical }));
    }

    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement('script');
      jsonLdEl.type = 'application/ld+json';
      jsonLdEl.dataset.page = 'newsletter';
      jsonLdEl.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prevTitle;
      // On ne supprime pas les meta mises à jour (les pages suivantes les écraseront).
      // En revanche, le JSON-LD spécifique à la page est nettoyé.
      if (jsonLdEl && jsonLdEl.parentNode) jsonLdEl.parentNode.removeChild(jsonLdEl);
    };
  }, [title, description, canonical, jsonLd]);
}

/**
 * Page Newsletter publique dédiée — /newsletter
 *
 * Objectifs :
 *   · SEO : mot-clé "newsletter code moto", page indexable avec contenu riche
 *   · Conversion : pitch clair des bénéfices + form proéminent + FAQ
 *   · Confiance : témoignages chiffrés + politique anti-spam claire
 *
 * Pas de tracking marketing, opt-in strict (RGPD).
 */
export default function Newsletter() {
  usePageMeta({
    title: 'Newsletter Code Moto 2026 — CODE 2 ROUES',
    description:
      'Reçois la newsletter CODE 2 ROUES : édition hebdo magazine, nouveaux contenus pédagogiques, alertes sécurité. 100% gratuit, max 2 emails/mois, désinscription en 1 clic.',
    canonical: 'https://code2roues.fr/newsletter',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Newsletter Code Moto 2026 — CODE 2 ROUES',
      description:
        "Reçois la newsletter pédagogique CODE 2 ROUES : édition hebdo magazine, nouveaux contenus pédagogiques, alertes sécurité. Gratuit, sans spam, désinscription en 1 clic.",
      url: 'https://code2roues.fr/newsletter',
      isPartOf: { '@type': 'WebSite', name: 'CODE 2 ROUES', url: 'https://code2roues.fr/' },
      mainEntity: {
        '@type': 'FAQPage',
        mainEntity: FAQ_ITEMS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    },
  });

  return (
      <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12" data-testid="newsletter-page">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors mb-6"
          data-testid="newsletter-back-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à l'accueil
        </Link>

        {/* HERO éditorial */}
        <section className="mb-10 sm:mb-14 grid lg:grid-cols-[1.5fr_1fr] gap-8 items-center" data-testid="newsletter-hero">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.32em] text-[hsl(var(--primary))] mb-3">
              <span className="h-px w-6 bg-[hsl(var(--primary))]" />
              Newsletter Code 2 Roues
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-black leading-[1.05] tracking-tight" data-testid="newsletter-title">
              Le 2 roues, <span className="text-[hsl(var(--primary))]">ta révision</span>, ton rythme.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-foreground/75 leading-relaxed">
              Une fois par semaine maximum, on te transmet l'essentiel de la pédagogie moto 2026 :
              une édition magazine, les nouveaux modules de révision, et les alertes de sécurité
              qui comptent vraiment.
            </p>
            <p className="mt-3 text-sm text-foreground/60 leading-relaxed">
              <strong className="text-foreground">100% gratuit · Pas de spam · Désinscription en 1 clic.</strong>{' '}
              Pensé par des moniteurs ECSR pour les futurs motards.
            </p>
          </div>

          {/* Trust pills — claims vérifiables uniquement */}
          <div className="grid grid-cols-3 gap-2.5 sm:gap-3" data-testid="newsletter-trust">
            <TrustPill kpi="0 €" label="Pour toujours" />
            <TrustPill kpi="ECSR" label="Moniteurs agréés" />
            <TrustPill kpi="2 max" label="Emails / mois" />
          </div>
        </section>

        {/* FORMULAIRE D'INSCRIPTION */}
        <section className="mb-12" data-testid="newsletter-form-section">
          <NewsletterForm
            variant="card"
            source="newsletter-page"
            title="Inscris-toi en 30 secondes"
            subtitle="Reçois ton premier email de bienvenue immédiatement avec les ressources clés pour bien démarrer ta révision."
          />
        </section>

        {/* 3 PILIERS */}
        <section className="mb-12 sm:mb-16" data-testid="newsletter-pillars">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.32em] text-[hsl(var(--primary))] mb-2">
              <span className="h-px w-6 bg-[hsl(var(--primary))]" />
              Ce que tu vas recevoir
              <span className="h-px w-6 bg-[hsl(var(--primary))]" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">3 piliers, jamais plus.</h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
            <PillarCard
              icon={Inbox}
              kicker="Hebdo"
              title="L'édition magazine"
              description="Chaque semaine, un dossier thématique ETM (sécurité, équipement, mécanique, environnement…) rédigé par des moniteurs."
            />
            <PillarCard
              icon={Target}
              kicker="Quand on en publie"
              title="Les nouveaux modules"
              description="Quiz par thème, enquêtes magazine, fiches plateau, fiches circulation… on te prévient dès qu'un nouveau module est disponible."
            />
            <PillarCard
              icon={ShieldAlert}
              kicker="Si nécessaire"
              title="Les alertes sécurité"
              description="Évolutions réglementaires, rappels constructeurs, pics d'accidentalité dans ta région. Que de l'utile."
            />
          </div>
        </section>

        {/* RÉASSURANCES (anti-spam, RGPD) */}
        <section className="mb-12 sm:mb-16 mc-card p-6 sm:p-8" data-testid="newsletter-reassurance">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
            <ReassureItem
              title="Pas de spam, jamais."
              description="Maximum 1 à 2 emails par mois. Pas de pubs tierces, pas de tracking publicitaire, pas de revente d'adresse."
            />
            <ReassureItem
              title="Désinscription 1 clic."
              description="Un lien dédié dans chaque email t'amène à la page de désinscription. Effective immédiatement, sans question."
            />
            <ReassureItem
              title="100% RGPD."
              description="Ton email est stocké en France, chiffré au repos, supprimé sous 30 jours après désinscription."
            />
            <ReassureItem
              title="Indépendant et gratuit."
              description="Aucun sponsor commercial dans nos contenus. Le projet vit grâce aux dons et aux partenariats pédagogiques uniquement."
            />
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12" data-testid="newsletter-faq">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.32em] text-[hsl(var(--primary))] mb-2">
              <span className="h-px w-6 bg-[hsl(var(--primary))]" />
              Questions fréquentes
              <span className="h-px w-6 bg-[hsl(var(--primary))]" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">Tout ce qu'on te garantit.</h2>
          </div>

          <div className="mc-card p-2 sm:p-4">
            <Accordion type="single" collapsible className="w-full">
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`newsletter-faq-${i}`}>
                  <AccordionTrigger className="text-left px-3 sm:px-4 hover:no-underline">
                    <span className="font-bold pr-4">{item.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-foreground/80 px-3 sm:px-4 pb-4">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA secondaire — re-form en bas pour les lecteurs qui ont scrollé */}
        <section className="mb-8" data-testid="newsletter-cta-bottom">
          <div className="mc-card p-6 sm:p-8 text-center bg-[hsl(var(--primary))]/5 border-[hsl(var(--primary))]/30">
            <Sparkles className="w-8 h-8 text-[hsl(var(--primary))] mx-auto mb-3" />
            <h2 className="text-xl sm:text-2xl font-black mb-2">Prêt à monter à bord ?</h2>
            <p className="text-sm text-foreground/70 mb-5 max-w-md mx-auto">
              Rejoins +12 000 motards qui révisent ETM avec nous. Premier email dans ta boîte dans 1 minute chrono.
            </p>
            <NewsletterForm
              variant="inline"
              source="newsletter-page-bottom"
              title=""
              subtitle=""
            />
          </div>
        </section>

        <p className="text-xs text-muted-foreground text-center">
          En t'inscrivant, tu acceptes notre{' '}
          <Link to="/confidentialite" className="underline hover:text-[hsl(var(--primary))]">politique de confidentialité</Link>.
          Tu peux te désinscrire à tout moment depuis{' '}
          <Link to="/newsletter/desinscription" className="underline hover:text-[hsl(var(--primary))]">cette page</Link>.
        </p>
      </div>
  );
}

// ============================================================
// FAQ data (extrait pour partage avec le JSON-LD FAQPage schema)
// ============================================================
const FAQ_ITEMS = [
  {
    q: "Quelle est la fréquence d'envoi ?",
    a: "Maximum 1 à 2 emails par mois. Concrètement, on envoie l'édition magazine hebdo seulement quand un dossier vraiment intéressant est prêt, et une notif quand un nouveau module de révision est disponible. Pas de remplissage.",
  },
  {
    q: 'Comment me désinscrire ?',
    a: "Un lien dédié figure dans le footer de chaque email. Un clic, ton email est saisi automatiquement, tu confirmes, c'est fait — effectif immédiatement. Tu peux aussi te désinscrire depuis la page /newsletter/desinscription en saisissant ton email.",
  },
  {
    q: 'Mes données sont-elles partagées ?',
    a: "Non, jamais. Ton email reste stocké uniquement sur nos serveurs (France, chiffré au repos), n'est revendu à aucun partenaire, et n'est utilisé qu'à des fins pédagogiques (envoi des emails de la newsletter Code 2 Roues).",
  },
  {
    q: 'Y a-t-il de la publicité dans les emails ?',
    a: "Non. Aucune pub commerciale tierce. Notre projet est indépendant et vit via les dons utilisateurs et les partenariats pédagogiques avec des écoles et constructeurs (jamais sponsorisés dans le contenu éditorial).",
  },
  {
    q: 'Je suis enseignant ECSR, ça me concerne aussi ?',
    a: "Oui ! La newsletter parle évolutions de la réglementation, programme officiel, ressources pédagogiques. Si tu veux du contenu plus pro (forum enseignants, ressources partagées), candidate à l'Espace Enseignant depuis la page de connexion.",
  },
  {
    q: "Vais-je recevoir des emails commerciaux ?",
    a: "Non. La newsletter est purement pédagogique. Si un jour on lance des produits payants (cours en ligne, accompagnement individualisé), tu recevras au maximum 1 annonce, dissociée de la newsletter classique, avec un opt-out clair.",
  },
];

// ============================================================
// Sub-components
// ============================================================
function TrustPill({ kpi, label }) {
  return (
    <div className="rounded-sm border border-border/70 bg-card px-2 py-3 sm:px-3 sm:py-4 text-center">
      <div className="text-lg sm:text-xl font-black tracking-tight text-foreground">{kpi}</div>
      <div className="mt-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground leading-tight">{label}</div>
    </div>
  );
}

function PillarCard({ icon: Icon, kicker, title, description }) {
  return (
    <div className="mc-card p-5 sm:p-6 h-full flex flex-col" data-testid={`pillar-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: 'hsl(var(--primary-soft))' }}>
        <Icon className="w-5 h-5 text-[hsl(var(--primary))]" />
      </div>
      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-1">{kicker}</div>
      <h3 className="text-base sm:text-lg font-black mb-2">{title}</h3>
      <p className="text-sm text-foreground/70 leading-relaxed flex-1">{description}</p>
    </div>
  );
}

function ReassureItem({ title, description }) {
  return (
    <div className="flex items-start gap-3" data-testid={`reassure-${title.toLowerCase().replace(/\s+/g, '-').replace(/[.,!]/g, '')}`}>
      <div className="w-6 h-6 rounded-full bg-green-500/15 text-green-600 dark:text-green-400 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Check className="w-3.5 h-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm mb-0.5">{title}</div>
        <p className="text-xs sm:text-sm text-foreground/70 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}
